import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DataTable.module.scss';
import Button, { ButtonToneProps } from '../Button/Button';
import ColumnSelectModal from '../../layouts/ColumnSelectModal/ColumnSelectModal';
import Icon from '../Icon/Icon';
import NoResultsFound from '../NoResultsFound/NoResultsFound';

interface DataTableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
  enableRowSelection?: boolean;
  sorting: SortingState;
  onSortingChange?: React.Dispatch<React.SetStateAction<SortingState>>;
  loading?: boolean;
  displayColumnSelect?: boolean;
  initialHiddenColumns?: string[];
  noResultsLabel?: string;
  emptyState?: React.ReactNode;
  onTableRowClick: (rowData: T) => void;
  onSelectedRowsChange?: (selected: T[]) => void;
  onColumnVisibilityChange?: (hiddenColumns: string[]) => void;
  rowSelection?: RowSelectionState;
  setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  getRowId?: (originalRow: T, index: number) => string;
}

export function DataTable<T>({
  columns,
  data,
  rowSelection,
  onTableRowClick,
  onSelectedRowsChange,
  sorting,
  onSortingChange,
  enableRowSelection = true,
  setRowSelection,
  loading,
  initialHiddenColumns,
  displayColumnSelect = true,
  noResultsLabel,
  emptyState,
  getRowId,
  onColumnVisibilityChange,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [showColumnSelect, setShowColumnSelect] = React.useState(false);
  const internalRowSelection = useState<RowSelectionState>({});

  const effectiveRowSelection = rowSelection ?? internalRowSelection[0];
  const setEffectiveRowSelection = setRowSelection ?? internalRowSelection[1];

  const initialColumnVisibility = useMemo(() => {
    if (!initialHiddenColumns) return {};
    return initialHiddenColumns.reduce<Record<string, boolean>>((acc, columnId) => {
      acc[columnId] = false;
      return acc;
    }, {});
  }, [initialHiddenColumns]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility);

  useEffect(() => {
    setColumnVisibility(initialColumnVisibility);
  }, [initialColumnVisibility]);

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection: effectiveRowSelection,
      sorting,
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getRowId,
    enableRowSelection,
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onRowSelectionChange: setEffectiveRowSelection,
    onSortingChange,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const selectedRows = useMemo(() => {
    return table.getSelectedRowModel().rows.map(row => row.original);
  }, [table, effectiveRowSelection]);

  useEffect(() => {
    onSelectedRowsChange?.(selectedRows);
  }, [onSelectedRowsChange, selectedRows]);

  useEffect(() => {
    const hiddenColumns = Object.entries(columnVisibility)
      .filter(([, visible]) => visible === false)
      .map(([key]) => key);

    onColumnVisibilityChange?.(hiddenColumns);
  }, [columnVisibility, onColumnVisibilityChange]);

  return (
    <div className={styles.wrapper}>
      {displayColumnSelect && (
        <div className={styles.columnSelectButton}>
          <Button tone={ButtonToneProps.SECONDARY} icon="tune" onClick={() => setShowColumnSelect(true)} />
        </div>
      )}

      {showColumnSelect && <ColumnSelectModal onClose={() => setShowColumnSelect(false)} table={table} />}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map(group => (
              <tr key={group.id}>
                {group.headers.map(header => (
                  <th key={header.id} className={styles.th}>
                    <div
                      className={styles.headerColumn}
                      role="button"
                      tabIndex={0}
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') header.column.getToggleSortingHandler()?.(e);
                      }}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <Icon name="accordion-arrow-up" />,
                        desc: <Icon name="accordion-arrow-down" />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id} onClick={() => onTableRowClick(row.original)}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className="p-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {data.length > 0 && table.getVisibleFlatColumns().some(column => column.columnDef.footer) && (
            <tfoot>
              {table.getFooterGroups().map(group => (
                <tr key={group.id}>
                  {group.headers.map(header => (
                    <td key={header.id} className="p-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
        {!loading && !data.length && (
          <div className={styles.noResultsFallback}>
            {emptyState ?? <NoResultsFound title={noResultsLabel || t('general.no_data')} />}
          </div>
        )}
      </div>
    </div>
  );
}
